/**
 * intenciones — broker v2 del puente (AC-3, orden 71cfa500).
 *
 * El vocabulario del puente cliente↔shell deja de vivir en constantes de código: son
 * INSTANCIAS de `clientes.intencion-puente.habilitada` — el UUID de la instancia ES el
 * `tipo` del postMessage; el alias es para humanos. Deshabilitar una instancia apaga
 * ese vocabulario en todos los clientes SIN deploy (gobernanza viva del canal).
 *
 * Validación de FORMA (anillo 2 del broker HOP): contra el shape DECLARADO de la
 * intención, con el vocabulario CERRADO de tipos (decisión A — isomorfo a
 * IndiceDeclaradoSchema): uuid / verbo_fsm / texto_corto. Claves exactas, todas
 * requeridas (decisión C: shapes estrictos, sin opcionales).
 *
 * Compat UNA versión (restricción de la orden): los literales __aarpia_accion__ y
 * __aarpia_print__ se aceptan mapeados a su alias con WARN de deprecación — retirar
 * en la siguiente (deuda con fecha en el reporte de cierre).
 */
import { api } from '@/lib/api'
import { vsn } from '@/lib/version'
import { logAarpia } from '@/lib/log'

export interface IntencionDecl {
  id: string
  alias: string
  direccion: 'iframe_a_shell' | 'shell_a_iframe'
  shape_exigido: Record<string, string>
  requiere_confirmacion: boolean
  verbo_max_alcance: 'lectura' | 'transicion' | 'chat' | 'ninguno'
}

export interface CatalogoIntenciones {
  porId: Map<string, IntencionDecl>
  porAlias: Map<string, IntencionDecl>
}

const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const RE_VERBO = /^[A-Z][A-Z0-9_]{1,39}$/
const MAX_TEXTO_CORTO = 500

/** Vocabulario CERRADO de tipos del shape (decisión A). Extenderlo = ACTUALIZAR
 *  gobernado del shape en el catálogo, jamás una constante nueva aquí. */
function valorValido(tipo: string, v: unknown): boolean {
  switch (tipo) {
    case 'uuid': return typeof v === 'string' && RE_UUID.test(v)
    case 'verbo_fsm': return typeof v === 'string' && RE_VERBO.test(v)
    case 'texto_corto': return typeof v === 'string' && v.length > 0 && v.length <= MAX_TEXTO_CORTO
    default: return false // tipo desconocido en el shape declarado → fail-closed
  }
}

/** Carga las intenciones HABILITADAS del catálogo vivo. Cache del caller (sesión). */
export async function cargarCatalogoIntenciones(token: string | null): Promise<CatalogoIntenciones> {
  const v = vsn('clientes', 'intencion-puente', 'habilitada', '1.2')
  const r = await api.get<any>(`/canonico/clientes/intencion-puente/habilitada/v${v}/instancias?limit=50`, token)
  const porId = new Map<string, IntencionDecl>()
  const porAlias = new Map<string, IntencionDecl>()
  for (const d of r?.data || []) {
    if (d?.attributes?.current_state !== 'habilitada') continue
    const p = d.attributes.payload || {}
    const it: IntencionDecl = {
      id: d.id,
      alias: String(p.alias || ''),
      direccion: p.direccion === 'shell_a_iframe' ? 'shell_a_iframe' : 'iframe_a_shell',
      shape_exigido: (p.shape_exigido && typeof p.shape_exigido === 'object') ? p.shape_exigido : {},
      requiere_confirmacion: !!p.requiere_confirmacion,
      verbo_max_alcance: p.verbo_max_alcance || 'ninguno',
    }
    porId.set(it.id, it)
    if (it.alias) porAlias.set(it.alias, it)
  }
  return { porId, porAlias }
}

// Compat UNA versión: literal legacy → alias del catálogo. Retirada programada.
const LEGACY: Record<string, string> = {
  __aarpia_accion__: 'accion',
  __aarpia_print__: 'imprimir',
}
let legacyAvisado = false

export type MensajeIntencion =
  | { ok: true; intencion: IntencionDecl; valores: Record<string, string>; legacy: boolean }
  | { ok: false; motivo: string }

/**
 * Valida un postMessage ENTRANTE (iframe→shell) contra el catálogo: tipo ∈ habilitadas,
 * dirección correcta, claves EXACTAS del shape declarado, valores del vocabulario
 * cerrado. Todo lo demás se descarta (el caller emite el WARN — nunca un error
 * inducible desde el HTML).
 */
export function validarMensajeIntencion(data: unknown, catalogo: CatalogoIntenciones): MensajeIntencion {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return { ok: false, motivo: 'no es objeto' }
  const d = data as Record<string, unknown>
  let tipo = d.tipo
  let legacy = false
  // Compat legacy: el mensaje viejo era {tipo:'__aarpia_accion__', instancia_id, verbo}.
  if (typeof tipo === 'string' && LEGACY[tipo]) {
    const it = catalogo.porAlias.get(LEGACY[tipo])
    if (!it) return { ok: false, motivo: `literal legacy '${tipo}' sin intención '${LEGACY[tipo]}' habilitada` }
    tipo = it.id
    legacy = true
    if (!legacyAvisado) {
      legacyAvisado = true
      logAarpia.warn(`Puente: literal legacy '${String(d.tipo)}' aceptado por compat UNA versión — migrar a UUID de intención (AC-3).`)
    }
  }
  if (typeof tipo !== 'string' || !RE_UUID.test(tipo)) return { ok: false, motivo: 'tipo no es UUID de intención' }
  const intencion = catalogo.porId.get(tipo)
  if (!intencion) return { ok: false, motivo: 'intención no habilitada en el catálogo' }
  if (intencion.direccion !== 'iframe_a_shell') return { ok: false, motivo: `intención '${intencion.alias}' es shell→iframe (no entrante)` }
  // Claves EXACTAS: tipo + las del shape declarado, ni una más ni una menos.
  const esperadas = new Set(['tipo', ...Object.keys(intencion.shape_exigido)])
  const presentes = Object.keys(d)
  if (presentes.length !== esperadas.size || presentes.some((k) => !esperadas.has(k))) {
    return { ok: false, motivo: 'claves del mensaje no coinciden con el shape declarado' }
  }
  const valores: Record<string, string> = {}
  for (const [clave, tipoValor] of Object.entries(intencion.shape_exigido)) {
    if (!valorValido(tipoValor, d[clave])) return { ok: false, motivo: `'${clave}' no cumple el tipo '${tipoValor}'` }
    valores[clave] = String(d[clave])
  }
  return { ok: true, intencion, valores, legacy }
}

/** Solo para tests: resetea el aviso de deprecación. */
export function _resetAvisoLegacy() { legacyAvisado = false }

// ── Cache de sesión del catálogo ─────────────────────────────────────────────
// Una carga por sesión (el catálogo cambia por acto de gobernanza, no por minuto);
// `invalidarCatalogoIntenciones()` fuerza recarga (p.ej. tras deshabilitar una intención).
let cachePromesa: Promise<CatalogoIntenciones> | null = null

export function obtenerCatalogoIntenciones(token: string | null): Promise<CatalogoIntenciones> {
  if (!cachePromesa) {
    cachePromesa = cargarCatalogoIntenciones(token).catch((e) => {
      cachePromesa = null // un fallo no envenena la sesión: el siguiente intento recarga
      throw e
    })
  }
  return cachePromesa
}

export function invalidarCatalogoIntenciones() { cachePromesa = null }
